import { useAuth } from "@/providers/auth";
import { useUser } from "@/providers/user";
import { LockClosedIcon } from "@radix-ui/react-icons";
import { useLocation } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

export default function AccessDenied() {
    const { user } = useUser();
    const { authenticated } = useAuth();
    const location = useLocation();

    if (!authenticated) {
        return null; // Laissez la logique de redirection gérer cela
    }

    return (
        <div className="flex items-center justify-center p-4">
            <Card className="w-full max-w-2xl">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                        <LockClosedIcon className="h-8 w-8 text-red-600" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-red-600">Access Denied</CardTitle>
                    <CardDescription className="text-lg">You don't have access to this object</CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                    <div className="rounded-lg bg-gray-50 p-4">
                        <h3 className="mb-3 font-semibold text-gray-900">Technical Information</h3>

                        <div className="space-y-3 text-sm">
                            <div className="flex items-start justify-between">
                                <span className="font-medium text-gray-700">Page URL:</span>
                                <span className="max-w-xs break-all text-gray-600">{location.pathname + location.search}</span>
                            </div>

                            <Separator />

                            <div className="flex items-center justify-between">
                                <span className="font-medium text-gray-700">User:</span>
                                <div className="flex items-center gap-2">
                                    <span className="text-gray-600">
                                        {user?.first_name} {user?.last_name}
                                    </span>
                                    <Badge variant="secondary">{user?.email}</Badge>
                                </div>
                            </div>

                            <Separator />

                            <div className="flex items-center justify-between">
                                <span className="font-medium text-gray-700">User Profile:</span>
                                <div className="flex flex-wrap gap-1 font-mono">{user?.profile}</div>
                            </div>

                            <Separator />

                            {user?.groups && (
                                <>
                                    <div className="flex items-center justify-between">
                                        <span className="font-medium text-gray-700">Groups:</span>
                                        <div className="flex flex-wrap gap-1 space-x-1">
                                            {user?.groups.map(group => (
                                                <span className="rounded-sm bg-slate-600 px-2 py-0.5 text-xs text-white" key={group}>
                                                    {group}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <Separator />
                                </>
                            )}

                            <div className="flex items-center justify-between">
                                <span className="font-medium text-gray-700">User ID:</span>
                                <span className="text-gray-600">{user?.id}</span>
                            </div>

                            <Separator />

                            <div className="flex items-center justify-between">
                                <span className="font-medium text-gray-700">Timestamp:</span>
                                <span className="text-gray-600">{new Date().toLocaleString()}</span>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg bg-blue-50 p-4">
                        <h3 className="mb-2 font-semibold text-blue-900">What to do next?</h3>
                        <ul className="space-y-1 text-sm text-blue-800">
                            <li>• Contact your administrator to request access to this resource</li>
                            <li>• Verify that you're logged in with the correct account</li>
                            <li>• Check if your user profile has the necessary permissions</li>
                            <li>• If you believe this is an error, please contact technical support</li>
                        </ul>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
